import React from 'react';
import { connect } from 'react-redux';
import AdminShell from './AdminShell';
import { Fieldset, SettingRow, Switch, Field, FormActions, FormCard } from './Form';

/* Mobile runtime settings backed by SiteSettings and consumed by both apps/API. */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const VERSIONS = ['userIosVersion', 'userAndroidVersion', 'partnerIosVersion', 'partnerAndroidVersion'];
const INTEGER_FIELDS = ['allowedServices', 'maximumEmergencyContact', 'maximumProviders'];
const TOGGLES = ['job', 'location', 'estimatedPrice', 'duration', 'description', 'photo',
  'sleepPartnerAndroid', 'sleepPartnerios', 'isRequestTimerToneEnable', 'openAppOnRequest'];
const EDITABLE = ['appForceUpdate'].concat(VERSIONS, [
  'allowableDistace', 'notificationInterval', 'allowedServices', 'defaultScheduleInterval',
  'addtionalWorkInterval', 'maximumEmergencyContact', 'maximumProviders'
], TOGGLES);

const Q = `query ($names: [String]) {
  getAdminSiteSettings(names: $names) {
    status errorMessage
    results { name value }
  }
}`;

const M = `mutation (${EDITABLE.map(f => `$${f}: String`).join(', ')}) {
  updateMobileSettings(${EDITABLE.map(f => `${f}: $${f}`).join(', ')}) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;
const isOn = value => String(value) === '1' || String(value) === 'true';
const decimal = value => /^\d+(\.\d+)?$/.test(String(value));

class MobileApps extends React.Component {
  state = {
    values: {}, original: {}, errors: {}, loading: true,
    error: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  isArabic = () => String(this.props.currentLocale || '').startsWith('ar');

  tx = (en, ar) => this.isArabic() ? ar : en;

  errorText = message => {
    if (!this.isArabic()) return message || 'Could not save the app settings.';
    const value = String(message || '').toLowerCase();
    if (value.includes('version')) return 'أدخل رقم إصدار صالحاً مثل 1.4.2.';
    if (value.includes('allowabledistace')) return 'يجب أن يكون نطاق البحث أكبر من صفر.';
    if (value.includes('notificationinterval')) return 'يجب ألا تقل مهلة طلب المهمة عن 0.5 دقيقة.';
    if (value.includes('defaultscheduleinterval')) return 'يجب ألا تقل خطوة الجدولة عن 20 دقيقة.';
    if (value.includes('addtionalworkinterval')) return 'يجب ألا تقل خطوة العمل الإضافي عن 0.5 ساعة.';
    if (value.includes('positive whole')) return 'أدخل عدداً صحيحاً موجباً.';
    return 'تعذّر حفظ إعدادات تطبيقات الجوال.';
  };

  load = async () => {
    this.setState({ loading: true, error: null });
    const json = await gql(Q, { names: EDITABLE }).catch(() => null);
    const res = json && json.data && json.data.getAdminSiteSettings;
    if (!ok(res)) {
      this.setState({
        loading: false,
        error: this.isArabic()
          ? 'تعذّر تحميل إعدادات تطبيقات الجوال.'
          : (res && res.errorMessage) || 'Could not load the app settings.'
      });
      return;
    }
    const values = {};
    (res.results || []).forEach(row => { values[row.name] = row.value === null ? '' : row.value; });
    this.setState({ values, original: { ...values }, errors: {}, loading: false });
  };

  set = (name, value) => this.setState(state => ({
    values: { ...state.values, [name]: value },
    errors: { ...state.errors, [name]: null },
    notice: null
  }));

  toggle = (name, on, trueValue = '1', falseValue = '0') =>
    this.set(name, on ? trueValue : falseValue);

  dirty() {
    const { values, original } = this.state;
    return EDITABLE.some(name => (values[name] || '') !== (original[name] || ''));
  }

  reset = () => this.setState(state => ({
    values: { ...state.original }, errors: {}, notice: null
  }));

  validate() {
    const { values } = this.state;
    const errors = {};
    const required = this.tx('This field is required.', 'هذا الحقل مطلوب.');
    VERSIONS.forEach(name => {
      const value = String(values[name] || '').trim();
      if (!value) errors[name] = required;
      else if (!/^\d+(\.\d+){0,2}$/.test(value)) {
        errors[name] = this.tx('Use a version such as 1.4.2.', 'استخدم إصداراً مثل 1.4.2.');
      }
    });
    INTEGER_FIELDS.forEach(name => {
      const value = String(values[name] || '').trim();
      if (!/^\d+$/.test(value) || Number(value) < 1) {
        errors[name] = this.tx('Enter a positive whole number.', 'أدخل عدداً صحيحاً موجباً.');
      }
    });
    const minimums = {
      allowableDistace: 0.000001,
      notificationInterval: 0.5,
      defaultScheduleInterval: 20,
      addtionalWorkInterval: 0.5
    };
    Object.keys(minimums).forEach(name => {
      const value = String(values[name] || '').trim();
      if (!decimal(value) || Number(value) < minimums[name]) {
        errors[name] = this.tx(`Enter ${minimums[name]} or more.`, `أدخل ${minimums[name]} أو أكثر.`);
      }
    });
    return errors;
  }

  save = async () => {
    if (this.state.saving) return;
    const errors = this.validate();
    if (Object.keys(errors).length) {
      this.setState({
        errors,
        notice: { tone: 'danger', text: this.tx('Check the highlighted fields.', 'تحقق من الحقول المحددة.') }
      });
      return;
    }
    const variables = EDITABLE.reduce((result, name) => {
      if ((this.state.values[name] || '') !== (this.state.original[name] || '')) {
        result[name] = String(this.state.values[name]).trim();
      }
      return result;
    }, {});
    this.setState({ saving: true, notice: null });
    const json = await gql(M, variables).catch(() => null);
    const res = json && json.data && json.data.updateMobileSettings;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ notice: { tone: 'danger', text: this.errorText(res && res.errorMessage) } });
      return;
    }
    this.setState(state => ({
      original: { ...state.original, ...variables },
      errors: {},
      notice: {
        tone: 'info',
        text: this.tx('App settings saved. Runtime controls apply when each app next loads its settings.',
          'تم حفظ إعدادات التطبيقات. تُطبّق إعدادات التشغيل عند تحميل كل تطبيق لإعداداته مرة أخرى.')
      }
    }));
  };

  textField = (name, label, hint) => {
    const id = `mobile-${name}`;
    return (
      <Field label={label} hint={hint} error={this.state.errors[name]} required htmlFor={id}>
        <input id={id} className="inp mono" dir="ltr" value={this.state.values[name] || ''}
          placeholder="1.0.0" maxLength="250" aria-invalid={!!this.state.errors[name]}
          onChange={event => this.set(name, event.target.value)} />
      </Field>
    );
  };

  numField = (name, label, hint, unit, options = {}) => {
    const id = `mobile-${name}`;
    return (
      <Field label={unit ? `${label} (${unit})` : label} hint={hint}
        error={this.state.errors[name]} required htmlFor={id}>
        <input id={id} className="inp" type="number" min={options.min || '0'}
          step={options.step || '1'} value={this.state.values[name] || ''}
          aria-invalid={!!this.state.errors[name]}
          onChange={event => this.set(name, event.target.value)} />
      </Field>
    );
  };

  render() {
    const { values, loading, error, saving, notice } = this.state;
    const forceOn = isOn(values.appForceUpdate);
    const jobCard = [
      ['job', this.tx('The job itself', 'المهمة نفسها')],
      ['location', this.tx('Pickup location', 'موقع الاستلام')],
      ['estimatedPrice', this.tx('Estimated fare', 'السعر التقديري')],
      ['duration', this.tx('Estimated duration', 'المدة التقديرية')],
      ['description', this.tx("Customer's description", 'وصف العميل')],
      ['photo', this.tx('Photos from the customer', 'صور العميل')]
    ];

    return (
      <AdminShell
        current="mobile"
        breadcrumb={[this.tx('System', 'النظام'), this.tx('Mobile apps', 'تطبيقات الجوال')]}
        title={this.tx('Mobile apps', 'تطبيقات الجوال')}
        lead={this.tx('Version gates and runtime behaviour for the customer and provider apps.',
          'حدود الإصدارات وسلوك التشغيل لتطبيقي العميل ومزوّد الخدمة.')}
        actions={[
          <a className="btn ghost" href="/siteadmin/settings/mobile-app" key="c">
            {this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}
          </a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`} role={notice.tone === 'danger' ? 'alert' : 'status'}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إغلاق')}</button>
          </div>
        )}

        {forceOn && (
          <div className="notice danger" role="alert">
            <span className="ni">!</span>
            <span className="nt">{this.tx(
              'Force update is on. Users below these minimum versions cannot use the app, so each version must already be live in its store.',
              'التحديث الإجباري مفعّل. لن يتمكن المستخدمون بإصدارات أقل من استخدام التطبيق، لذلك يجب أن تكون الإصدارات المحددة منشورة في المتاجر.'
            )}</span>
          </div>
        )}

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">{this.tx(
            'Location ping interval, in-app chat and tipping have no global setting. Scheduled and direct bookings are configured per service on the Services screen.',
            'لا توجد إعدادات عامة لفاصل تحديث الموقع أو المحادثة داخل التطبيق أو الإكراميات. يتم ضبط الحجوزات المجدولة والمباشرة لكل خدمة من شاشة الخدمات.'
          )}</span>
        </div>

        {error && (
          <div className="notice danger" role="alert">
            <span className="ni">!</span><span className="nt">{error}</span>
            <button className="btn ghost sm" type="button" onClick={this.load}>
              {this.tx('Try again', 'إعادة المحاولة')}
            </button>
          </div>
        )}

        {loading
          ? <section className="card"><div className="card-b">{this.tx('Loading app settings…', 'جارٍ تحميل إعدادات التطبيقات…')}</div></section>
          : !error && (
            <FormCard actions={
              <FormActions
                note={this.tx('Only changed settings are written.', 'يتم حفظ الإعدادات التي تغيرت فقط.')}
                dirty={this.dirty()} busy={saving} onSave={this.save} onCancel={this.reset}
                saveLabel={this.tx('Save app settings', 'حفظ إعدادات التطبيقات')}
                cancelLabel={this.tx('Cancel', 'إلغاء')}
                busyLabel={this.tx('Saving…', 'جارٍ الحفظ…')}
              />
            }>
              <Fieldset title={this.tx('Minimum versions', 'الحد الأدنى للإصدارات')}
                desc={this.tx('With force update enabled, an app below its threshold cannot continue.', 'عند تفعيل التحديث الإجباري، لا يمكن للتطبيق الأقل من الحد المحدد المتابعة.')}>
                {this.textField('userIosVersion', this.tx('Customer app — iOS', 'تطبيق العميل — iOS'))}
                {this.textField('userAndroidVersion', this.tx('Customer app — Android', 'تطبيق العميل — Android'))}
                {this.textField('partnerIosVersion', this.tx('Provider app — iOS', 'تطبيق مزوّد الخدمة — iOS'))}
                {this.textField('partnerAndroidVersion', this.tx('Provider app — Android', 'تطبيق مزوّد الخدمة — Android'))}
              </Fieldset>

              <Fieldset rows>
                <SettingRow label={this.tx('Force update', 'التحديث الإجباري')}
                  hint={this.tx('When off, minimum versions are recorded but do not block an app.', 'عند إيقافه، تُحفظ الإصدارات الدنيا دون حظر التطبيق.')}>
                  <Switch checked={forceOn} label={this.tx('Force update', 'التحديث الإجباري')}
                    onChange={value => this.toggle('appForceUpdate', value, 'true', 'false')} />
                </SettingRow>
              </Fieldset>

              <Fieldset title={this.tx('Dispatch', 'توزيع الطلبات')}>
                {this.numField('allowableDistace', this.tx('Search radius', 'نطاق البحث'),
                  this.tx('Maximum distance used when finding nearby providers.', 'أقصى مسافة للبحث عن مزوّدي الخدمة القريبين.'), this.tx('km', 'كم'), { step: '0.1', min: '0.1' })}
                {this.numField('notificationInterval', this.tx('Job request window', 'مهلة طلب المهمة'),
                  this.tx('How long a provider can respond before the request expires.', 'المدة المتاحة لمزوّد الخدمة للرد قبل انتهاء الطلب.'), this.tx('minutes', 'دقائق'), { step: '0.5', min: '0.5' })}
                {this.numField('allowedServices', this.tx('Services per booking', 'الخدمات لكل حجز'),
                  this.tx('Maximum services a customer can add to one booking.', 'أقصى عدد خدمات يمكن للعميل إضافتها إلى حجز واحد.'))}
                {this.numField('maximumProviders', this.tx('Providers tried per booking', 'مزوّدو الخدمة لكل حجز'),
                  this.tx('Maximum nearby providers considered during assignment.', 'أقصى عدد من مزوّدي الخدمة القريبين الذين تتم تجربتهم أثناء التعيين.'))}
                {this.numField('defaultScheduleInterval', this.tx('Scheduling step', 'خطوة الجدولة'),
                  this.tx('Gap between selectable times for scheduled bookings.', 'الفاصل بين الأوقات المتاحة للحجوزات المجدولة.'), this.tx('minutes', 'دقائق'), { min: '20' })}
                {this.numField('addtionalWorkInterval', this.tx('Additional work step', 'خطوة العمل الإضافي'),
                  this.tx('Time unit added when a job runs longer.', 'وحدة الوقت المضافة عندما تستغرق المهمة وقتاً أطول.'), this.tx('hours', 'ساعات'), { step: '0.5', min: '0.5' })}
                {this.numField('maximumEmergencyContact', this.tx('Emergency contacts per customer', 'جهات الطوارئ لكل عميل'))}
              </Fieldset>

              <Fieldset title={this.tx('Job request details', 'تفاصيل طلب المهمة')} rows
                desc={this.tx('Controls which details appear on the incoming job card in the provider app.', 'يحدد التفاصيل التي تظهر في بطاقة المهمة الواردة في تطبيق مزوّد الخدمة.')}>
                {jobCard.map(([key, label]) => (
                  <SettingRow key={key} label={label}>
                    <Switch checked={isOn(values[key])} label={label}
                      onChange={value => this.toggle(key, value)} />
                  </SettingRow>
                ))}
              </Fieldset>

              <Fieldset title={this.tx('Provider app behaviour', 'سلوك تطبيق مزوّد الخدمة')} rows>
                <SettingRow label={this.tx('Keep screen awake — Android', 'إبقاء الشاشة نشطة — Android')}
                  hint={this.tx('Prevents sleep while the provider is on duty.', 'يمنع إطفاء الشاشة أثناء عمل مزوّد الخدمة.')}>
                  <Switch checked={isOn(values.sleepPartnerAndroid)} label={this.tx('Keep screen awake Android', 'إبقاء شاشة Android نشطة')}
                    onChange={value => this.toggle('sleepPartnerAndroid', value)} />
                </SettingRow>
                <SettingRow label={this.tx('Keep screen awake — iOS', 'إبقاء الشاشة نشطة — iOS')}>
                  <Switch checked={isOn(values.sleepPartnerios)} label={this.tx('Keep screen awake iOS', 'إبقاء شاشة iOS نشطة')}
                    onChange={value => this.toggle('sleepPartnerios', value)} />
                </SettingRow>
                <SettingRow label={this.tx('Play a tone for a new request', 'تشغيل نغمة لطلب جديد')}>
                  <Switch checked={isOn(values.isRequestTimerToneEnable)} label={this.tx('Request tone', 'نغمة الطلب')}
                    onChange={value => this.toggle('isRequestTimerToneEnable', value)} />
                </SettingRow>
                <SettingRow label={this.tx('Open the app for a new request', 'فتح التطبيق عند وصول طلب جديد')}
                  hint={this.tx('Brings the app forward instead of only showing a notification.', 'يعرض التطبيق في الواجهة بدلاً من إظهار إشعار فقط.')}>
                  <Switch checked={isOn(values.openAppOnRequest)} label={this.tx('Open on request', 'فتح عند الطلب')}
                    onChange={value => this.toggle('openAppOnRequest', value)} />
                </SettingRow>
              </Fieldset>
            </FormCard>
          )}
      </AdminShell>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });

export default connect(mapState)(MobileApps);
